"use client";

import { cloneElement, Fragment, isValidElement, type ReactElement, type ReactNode } from "react";

import { findPublicIds, type PublicIdHrefResolver } from "../lib/public-id-links";

/**
 * Renderer half of public-id autolinking: swaps bare ids in a rendered node's
 * children for links to the resource they name.
 *
 * This deliberately runs at the component layer rather than as a remark plugin.
 * Streamdown memoizes the parsed mdast by content across component instances,
 * so a remark plugin that rewrote text nodes would bake the first caller's
 * hrefs into a tree later renders reuse — including renders in another
 * workspace, or with no resolver at all. `components` re-run on every render,
 * so the href always matches the workspace the reader is actually in.
 *
 * String children are rewritten; wrappers like `<strong>` are cloned so the
 * walk continues. Anchors and code are left alone — nesting an `<a>` inside
 * an `<a>` is invalid, and a backticked id is a literal, not a link.
 */
const SKIP_TAGS = new Set(["a", "code", "pre"]);

type LinkifyElement = ReactElement<{
  children?: ReactNode;
  // Streamdown puts the hast element on every component; function components
  // (inlineCode, strong) don't have a string `type`, so this is how we know
  // not to walk into a code span or an existing link.
  node?: { tagName?: string };
}>;

function hostTag(element: LinkifyElement): string | undefined {
  if (typeof element.type === "string") return element.type;
  return element.props.node?.tagName;
}

export function linkifyPublicIds(
  children: ReactNode,
  resolve: PublicIdHrefResolver | null,
  linkClassName?: string,
): ReactNode {
  if (!resolve) return children;
  return linkifyNode(children, resolve, linkClassName);
}

function linkifyNode(
  node: ReactNode,
  resolve: PublicIdHrefResolver,
  linkClassName?: string,
): ReactNode {
  if (typeof node === "string") return linkifyString(node, resolve, linkClassName);
  if (Array.isArray(node)) {
    let changed = false;
    const mapped = node.map((child, index) => {
      const replacement = linkifyNode(child, resolve, linkClassName);
      if (replacement === child) return child;
      changed = true;
      return typeof child === "string" ? (
        <Fragment key={`public-id-${index}`}>{replacement}</Fragment>
      ) : (
        replacement
      );
    });
    return changed ? mapped : node;
  }
  if (!isValidElement<LinkifyElement["props"]>(node)) return node;
  const tag = hostTag(node);
  if (tag && SKIP_TAGS.has(tag)) return node;
  const nextChildren = linkifyNode(node.props.children, resolve, linkClassName);
  if (nextChildren === node.props.children) return node;
  return cloneElement(node, undefined, nextChildren);
}

function linkifyString(
  text: string,
  resolve: PublicIdHrefResolver,
  linkClassName?: string,
): ReactNode {
  const matches = findPublicIds(text).filter((match) => resolve(match.id, match.type));
  if (matches.length === 0) return text;

  const parts: ReactNode[] = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.start > cursor) parts.push(text.slice(cursor, match.start));
    parts.push(
      <a
        key={`${match.id}-${match.start}`}
        href={resolve(match.id, match.type)}
        // Opens beside whatever the reader is on rather than navigating them
        // out of a task or thread they are part-way through.
        target="_blank"
        rel="noopener noreferrer"
        title={`Open ${match.type.replace(/_/g, " ")} ${match.id}`}
        className={linkClassName}
      >
        {match.id}
      </a>,
    );
    cursor = match.end;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}
